import { useState } from "react";
import type { StateAction, StateMapping } from "@jump-pipelines/core";
import type { WidgetProps } from "../../lib/schema-form/types";
import { InfoTip } from "../primitives/InfoTip";
import {
  ADOPTION_DELETE_CAUTION,
  adoptionRefusalNotice,
  DRY_RUN_BANNER,
  DRY_RUN_DEMOTE,
  DRY_RUN_PROMOTE,
  NO_STAGE_LABEL,
  SUGGESTED_STATES,
  actionOptions,
  archiveCaution,
  copyFor,
  firstEnableMapping,
  blankMapping,
} from "./state-mapping-copy";

/** What the pages hand this widget under `widgetContext.stateMapping`. */
export interface StateMappingContext {
  /** From the transform's registry metadata. Present ⇒ render the stage column. */
  stateField?: string;
  /** From the transform's registry metadata. True ⇒ offer "remove". */
  canArchive?: boolean;
  destObjectType?: string;
  /** Stage options from the destination's `dealPipelines` discover. Empty ⇒ free text. */
  stages: Array<{ id: string; name: string; description?: string }>;
  defaultStateStages?: Record<string, string>;
  /** The pipeline's legacy `dealStageByStatus`, folded in on first enable. */
  legacyStageMap?: Record<string, string>;
  /** True ⇒ the pipeline has record adoption configured, so it writes to records
   *  the customer created (spec §14). The same fact the HubSpot deliver acts on,
   *  surfaced to the person choosing the rule: a delete rule is what lets those
   *  records be deleted. */
  adoptionEnabled?: boolean;
}

const DATALIST_ID = "statemap-states";

function asMapping(value: unknown): StateMapping | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null;
  const rules = (value as { rules?: unknown }).rules;
  if (!Array.isArray(rules)) return null;
  return value as StateMapping;
}

export function StateMappingField({ value, onChange, context }: WidgetProps) {
  const ctx = ((context as { stateMapping?: StateMappingContext })?.stateMapping) ?? { stages: [] };
  const copy = copyFor(ctx.destObjectType);
  const mapping = asMapping(value);
  const hasStages = ctx.stateField !== undefined;
  const [enableError, setEnableError] = useState<string | null>(null);

  function update(next: StateMapping) {
    onChange(next);
  }

  // First enable writes the pipeline's CURRENT effective behavior, so turning
  // the feature on is a no-op until a row changes (spec Goal 5).
  //
  // The pre-fill refuses to build a mapping larger than the schema accepts, so
  // a legacy stage map with too many distinct states throws here rather than
  // producing a table that fails on save. Surface that instead of letting it
  // escape the click handler.
  //
  // Surfacing alone would strand the operator: this branch's only action is
  // this same function, so a retry re-runs the conversion that just failed.
  // `startEmpty` is the way out — it skips the pre-fill and opens the table
  // with one blank row, the same shape a pipeline with no legacy behavior
  // gets, so the mapping can still be built by hand.
  function enable() {
    try {
      update(firstEnableMapping({ legacyStageMap: ctx.legacyStageMap, defaultStateStages: ctx.defaultStateStages }));
      setEnableError(null);
    } catch (err) {
      setEnableError(err instanceof Error ? err.message : String(err));
    }
  }

  function startEmpty() {
    update(blankMapping());
    setEnableError(null);
  }

  // Spec §14, on the screen the operator would look at. An adopting pipeline's
  // built-in cancellation removal is refused, so with no delete rule a cancelled
  // item stays put — otherwise visible only in a log line, a `skipped` run and a
  // monitor. Gated on `canArchive` too: nothing is being refused on an object
  // type that could never be removed.
  const refusesRemoval = ctx.adoptionEnabled === true && ctx.canArchive === true;

  if (!mapping) {
    return (
      <div className="statemap">
        <div className="statemap__head uppercase-label">
          {copy.heading} <InfoTip termKey={copy.termKey} />
        </div>
        <div className="statemap__empty">
          No status rules. Every status updates as usual.
          {refusesRemoval && ` ${adoptionRefusalNotice(copy)}`}
        </div>
        {enableError && <div className="field__error">{enableError}</div>}
        <button type="button" className="btn btn--secondary statemap__add" onClick={enable}>
          + Add a status
        </button>
        {enableError && (
          <button type="button" className="btn btn--secondary statemap__add" onClick={startEmpty}>
            Start empty
          </button>
        )}
      </div>
    );
  }

  // `mapping` narrows to non-null above, but that narrowing doesn't survive
  // into the function declarations below (they're closures the compiler can't
  // prove run before any reassignment) — so capture a plainly-typed alias
  // instead of asserting non-null at every use.
  const current: StateMapping = mapping;

  function setRule(i: number, patch: Partial<StateMapping["rules"][number]>) {
    update({
      ...current,
      rules: current.rules.map((r, idx) => {
        if (idx !== i) return r;
        const next = { ...r, ...patch };
        // A stage is only meaningful on an update; carrying one onto a skip or a
        // remove would be rejected at save with an issue the operator can't see.
        if (next.action !== "sync") delete next.stage;
        return next;
      }),
    });
  }

  function addRule() {
    update({ ...current, rules: [...current.rules, { state: "", action: "sync" }] });
  }

  function removeRule(i: number) {
    update({ ...current, rules: current.rules.filter((_, idx) => idx !== i) });
  }

  const anyArchive = current.rules.some((r) => r.action === "archive");

  return (
    <div className="statemap">
      <div className="statemap__head uppercase-label">
        {copy.heading} <InfoTip termKey={copy.termKey} />
      </div>
      <p className="statemap__lede">This pipeline only changes what you tell it to.</p>

      {current.mode === "dry-run" ? (
        <div className="statemap__dryrun">
          <span>
            {DRY_RUN_BANNER} <InfoTip termKey="dryRun" />
          </span>
          <button
            type="button"
            className="btn btn--primary statemap__promote"
            onClick={() => update({ ...current, mode: "active" })}
          >
            {DRY_RUN_PROMOTE}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--ghost statemap__demote"
          onClick={() => update({ ...current, mode: "dry-run" })}
        >
          {DRY_RUN_DEMOTE}
        </button>
      )}

      <datalist id={DATALIST_ID}>
        {SUGGESTED_STATES.map((s) => <option key={s} value={s} />)}
      </datalist>

      <table className="statemap__table">
        <thead>
          <tr>
            <th className="uppercase-label">Status</th>
            <th className="uppercase-label">
              What happens
              {/* BRAND.md asks for the ⓘ on the term's primary appearance — not
                  where the term does not appear. `actionOptions` withholds the
                  delete option unless the destination declares `canArchive`, so
                  on a deal the definition would sit beside a column that offers
                  no such choice. Gated on the same fact. */}
              {ctx.canArchive === true && <> <InfoTip termKey="deleteRecord" /></>}
            </th>
            {hasStages && (
              <th className="uppercase-label">
                {copy.stageColumnLabel} <InfoTip termKey="dealStage" />
              </th>
            )}
            <th />
          </tr>
        </thead>
        <tbody>
          {mapping.rules.map((rule, i) => (
            <tr key={i}>
              <td>
                <input
                  className="field__input"
                  aria-label={`status ${i + 1}`}
                  list={DATALIST_ID}
                  value={rule.state}
                  onChange={(e) => setRule(i, { state: e.target.value })}
                />
              </td>
              <td>
                <select
                  className="field__input"
                  aria-label={`what happens ${i + 1}`}
                  value={rule.action}
                  onChange={(e) => setRule(i, { action: e.target.value as StateAction })}
                >
                  {actionOptions(copy, { canArchive: ctx.canArchive }).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </td>
              {hasStages && (
                <td>
                  {rule.action !== "sync" ? (
                    // Nothing to set: the stage only applies to an update. Says so
                    // rather than showing a control that would do nothing.
                    <span className="statemap__stage-na">{NO_STAGE_LABEL}</span>
                  ) : ctx.stages.length > 0 ? (
                    <select
                      className="field__input"
                      aria-label={`deal stage ${i + 1}`}
                      value={rule.stage ?? ""}
                      onChange={(e) => setRule(i, { stage: e.target.value === "" ? undefined : e.target.value })}
                    >
                      <option value="">{NO_STAGE_LABEL}</option>
                      {ctx.stages.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.description ? `${s.name} — ${s.description}` : s.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // Discover failed or the portal published nothing. Free text,
                    // with the placeholder carrying the no-change meaning so the
                    // cell never reads as an unset blank. Save-time checks warn.
                    <input
                      className="field__input"
                      aria-label={`deal stage ${i + 1}`}
                      placeholder={NO_STAGE_LABEL}
                      value={rule.stage ?? ""}
                      onChange={(e) => setRule(i, { stage: e.target.value === "" ? undefined : e.target.value })}
                    />
                  )}
                </td>
              )}
              <td>
                <button
                  type="button"
                  className="btn btn--ghost"
                  aria-label={`remove status ${i + 1}`}
                  onClick={() => removeRule(i)}
                >
                  REMOVE
                </button>
              </td>
            </tr>
          ))}
          <tr className="statemap__fallback">
            <td>Anything else</td>
            <td>
              <select
                className="field__input"
                aria-label="what happens for anything else"
                value={mapping.unmapped}
                onChange={(e) => update({ ...mapping, unmapped: e.target.value as "sync" | "skip" })}
              >
                {actionOptions(copy, { canArchive: ctx.canArchive, isFallback: true }).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </td>
            {hasStages && <td><span className="statemap__stage-na">{NO_STAGE_LABEL}</span></td>}
            <td />
          </tr>
        </tbody>
      </table>

      {anyArchive ? (
        <p className="statemap__caution">
          {archiveCaution(copy)}
          {ctx.adoptionEnabled === true && ` ${ADOPTION_DELETE_CAUTION}`}
        </p>
      ) : refusesRemoval ? (
        <p className="statemap__caution">{adoptionRefusalNotice(copy)}</p>
      ) : null}

      <button type="button" className="btn btn--secondary statemap__add" onClick={addRule}>
        + Add a status
      </button>
    </div>
  );
}
